import { styled } from '@porsche-code/kado-styled-system/jsx';
import { Spacer } from '@porsche-code/kado-ui';
import KadoLogo from '@porsche-code/kado-ui/icons/kado.svg?react';
import { PHeading } from '@porsche-design-system/components-react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  Announcement,
  ApplicationTile,
  BrowserSupport,
  FeatureArticleTable,
  UserFeedback,
} from '@/components';
import { App, AppLink } from '@/config/app';
import { NAMESPACES } from '@/config/i18n';
import { Role } from '@/config/role';
import { usePersonContext } from '@/context';

export const Route = createFileRoute('/')({
  component: Dashboard,
});

function Dashboard() {
  const { t } = useTranslation(NAMESPACES);

  const {
    state: { roles },
  } = usePersonContext();

  return (
    <>
      <UserFeedback />
      <PHeading
        align="center"
        aria-level={1}
        tag="h1"
        size="2xl"
        weight="semibold"
      >
        <KadoLogo aria-hidden style={{ verticalAlign: 'middle' }} />
        <span style={{ verticalAlign: 'middle' }}>{t('common:title')}</span>
      </PHeading>
      <Spacer mb="lg" />
      <ContentWrapper>
        <Announcement />

        <ApplicationGroup>
          <ApplicationTile application={App.FieldForceManagement} />
          <ApplicationTile application={App.Greenland} />
          <ApplicationTile application={App.Survey} />
          {/* Temporarily hide the application tile for other roles */}
          {[Role.SystemAdmin, Role.Mpi].some((role) =>
            roles?.includes(role),
          ) ? (
            <ApplicationTile application={AppLink.Mpi} />
          ) : null}
        </ApplicationGroup>

        <Spacer mb="lg" />

        <PHeading tag="h3" aria-level={3} size="md">
          {t('applicationGroup.strategyPlanning')}
        </PHeading>

        <Spacer mb="md" />

        <ApplicationGroup>
          <ApplicationTile application={App.PorscheStrategy2030} />
          <ApplicationTile application={App.PcRoadmap} />
          <ApplicationTile application={App.PorscheImportAgreements} />
        </ApplicationGroup>

        <Spacer mb="lg" />

        <PHeading tag="h3" aria-level={3} size="md">
          {t('applicationGroup.networkRetailDesign')}
        </PHeading>

        <Spacer mb="md" />

        <ApplicationGroup>
          <ApplicationTile application={App.NetworkPlanning} />
          <ApplicationTile application={App.RetailDesign} />
          <ApplicationTile application={App.MultisensoryExperience} />
          <ApplicationTile application={App.Apex} />
        </ApplicationGroup>

        <Spacer mb="lg" />

        <PHeading align="center" aria-level={2} tag="h2" size="lg">
          {t('featureArticles')}
        </PHeading>

        <Spacer mb="md" />

        <FeatureArticleTable />
      </ContentWrapper>
      <BrowserSupport />
    </>
  );
}

const ContentWrapper = styled('div', {
  base: {
    maxWidth: '1390px',
    margin: '0 auto',
  },
});

const ApplicationGroup = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 'lg',
    sm: {
      gridTemplateColumns: '1fr 1fr',
    },
    md: {
      gridTemplateColumns: '1fr 1fr 1fr',
    },
    lg: {
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
    },
  },
});
